'use client'

import { useEffect, useState } from 'react'
import { useAuth } from '@/store/hooks'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
    MdAdd,
    MdSearch,
    MdEdit,
    MdDelete,
    MdLocalPharmacy,
    MdClose,
    MdCheckCircle,
    MdCancel,
    MdRefresh,
    MdReceipt,
} from 'react-icons/md'
import ConfirmModal from '@/components/confirm-modal'
import {
    getPharmacyIntegrations,
    deletePharmacyIntegration,
    testPharmacyIntegration,
    getPharmacyIntegrationsByCompany,
    PharmacyIntegration,
    PHARMACY_TYPE_LABELS,
} from '@/lib/api/health-integrations/pharmacy-api'
import { ShareableModule } from '@/lib/api/share/share-api'
import { MODULE_LABELS } from '@/hooks/useShareData'
import { ImportDataModal } from '@/components/import/ImportDataModal'
import { MdFileDownload } from 'react-icons/md'

export default function PharmacyPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { canCreate, canUpdate, canDelete } = useResourcePermissions(
        'health_integrations',
        'pharmacy'
    )

    const [integrations, setIntegrations] = useState<PharmacyIntegration[]>([])
    const [filteredIntegrations, setFilteredIntegrations] = useState<
        PharmacyIntegration[]
    >([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [integrationToDelete, setIntegrationToDelete] = useState<
        number | null
    >(null)
    const [deleting, setDeleting] = useState(false)
    const [testing, setTesting] = useState<number | null>(null)
    const [showImportModal, setShowImportModal] = useState(false)

    useEffect(() => {
        if (user?.companyId) {
            loadIntegrations()
        }
    }, [user?.companyId])

    useEffect(() => {
        if (searchTerm.trim()) {
            const filtered = integrations.filter(
                integration =>
                    integration.name
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase()) ||
                    PHARMACY_TYPE_LABELS[integration.type]
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase())
            )
            setFilteredIntegrations(filtered)
        } else {
            setFilteredIntegrations(integrations)
        }
    }, [searchTerm, integrations])

    const loadIntegrations = async () => {
        setLoading(true)
        try {
            const response = await getPharmacyIntegrations()
            if (response.success) {
                setIntegrations(response.result)
                setFilteredIntegrations(response.result)
            } else {
                toast.error(
                    response.error || 'Erro ao carregar integrações de farmácia'
                )
            }
        } catch (error) {
            toast.error('Erro ao carregar integrações de farmácia')
        } finally {
            setLoading(false)
        }
    }

    const handleTest = async (id: number) => {
        setTesting(id)
        try {
            const response = await testPharmacyIntegration(id)
            if (response.success && response.result.success) {
                toast.success('Conexão testada com sucesso')
            } else {
                toast.error(
                    response.result?.message || 'Falha no teste de conexão'
                )
            }
        } catch (error) {
            toast.error('Erro ao testar conexão')
        } finally {
            setTesting(null)
        }
    }

    const handleDelete = (id: number) => {
        setIntegrationToDelete(id)
        setShowDeleteModal(true)
    }

    const confirmDelete = async () => {
        if (!integrationToDelete) return

        setDeleting(true)
        try {
            const response =
                await deletePharmacyIntegration(integrationToDelete)
            if (response.success) {
                toast.success('Integração excluída com sucesso')
                loadIntegrations()
                setShowDeleteModal(false)
                setIntegrationToDelete(null)
            } else {
                toast.error(response.error || 'Erro ao excluir integração')
            }
        } catch (error) {
            toast.error('Erro ao excluir integração')
        } finally {
            setDeleting(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando integrações...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-lg">
                        <MdLocalPharmacy className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-base-content">
                            Receita Eletrônica
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {integrations.length}{' '}
                            {integrations.length === 1
                                ? 'integração'
                                : 'integrações'}{' '}
                            cadastradas
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        className="btn btn-ghost btn-sm gap-2"
                        onClick={() =>
                            navigate('/health-integrations/pharmacy/receitas')
                        }
                    >
                        <MdReceipt className="text-xl" />
                        <span className="hidden sm:inline">Receitas</span>
                    </button>
                    <button
                        className="btn btn-ghost btn-sm gap-2"
                        onClick={() => setShowImportModal(true)}
                    >
                        <MdFileDownload className="text-xl" />
                        <span className="hidden sm:inline">Importar</span>
                    </button>
                    {canCreate && (
                        <button
                            className="btn btn-secondary gap-2"
                            onClick={() =>
                                navigate('/health-integrations/pharmacy/new')
                            }
                        >
                            <MdAdd className="text-xl" />
                            <span className="hidden sm:inline">Adicionar</span>
                        </button>
                    )}
                </div>
            </div>

            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4">
                <div className="flex items-center gap-2">
                    <div className="flex-1 relative">
                        <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/70 text-xl pointer-events-none z-10" />
                        <input
                            type="text"
                            placeholder="Buscar por nome ou tipo..."
                            className="input input-bordered w-full pl-10"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                    </div>
                    {searchTerm && (
                        <button
                            type="button"
                            onClick={() => setSearchTerm('')}
                            className="btn btn-ghost btn-sm"
                        >
                            <MdClose className="text-lg" />
                        </button>
                    )}
                </div>
            </div>

            {filteredIntegrations.length === 0 ? (
                <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 bg-base-100 rounded-xl border border-base-300/50 p-8">
                    <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                        <MdLocalPharmacy className="text-4xl text-base-content/30" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-base-content mb-1">
                            {searchTerm
                                ? 'Nenhuma integração encontrada'
                                : 'Nenhuma integração cadastrada'}
                        </h3>
                        <p className="text-sm text-base-content/60 mb-4">
                            {searchTerm
                                ? 'Tente ajustar sua busca'
                                : 'Configure integrações para emitir receitas eletrônicas'}
                        </p>
                        {!searchTerm && canCreate && (
                            <button
                                onClick={() =>
                                    navigate(
                                        '/health-integrations/pharmacy/new'
                                    )
                                }
                                className="btn btn-secondary gap-2"
                            >
                                <MdAdd className="text-xl" />
                                Adicionar integração
                            </button>
                        )}
                    </div>
                </div>
            ) : (
                <div className="flex flex-col gap-4">
                    {filteredIntegrations.map(integration => (
                        <div
                            key={integration.id}
                            className="group bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 overflow-hidden hover:shadow-lg transition-all duration-300"
                        >
                            <div className="flex items-start gap-4 p-4">
                                <div className="relative w-16 h-16 flex-shrink-0 bg-gradient-to-br from-purple-500/20 to-purple-500/10 rounded-xl flex items-center justify-center border border-purple-500/20">
                                    <MdLocalPharmacy className="text-3xl text-purple-500" />
                                </div>

                                <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between gap-2 mb-2">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <h3 className="text-base font-bold text-base-content break-words">
                                                    {integration.name}
                                                </h3>
                                                {integration.active ? (
                                                    <span className="badge badge-success badge-sm gap-1">
                                                        <MdCheckCircle className="text-xs" />
                                                        Ativa
                                                    </span>
                                                ) : (
                                                    <span className="badge badge-ghost badge-sm gap-1">
                                                        <MdCancel className="text-xs" />
                                                        Inativa
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-sm text-base-content/70 mt-1">
                                                Tipo:{' '}
                                                {
                                                    PHARMACY_TYPE_LABELS[
                                                        integration.type
                                                    ]
                                                }
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex flex-wrap items-center gap-2 mb-3">
                                        {integration._count && (
                                            <span className="badge badge-outline badge-sm">
                                                {
                                                    integration._count
                                                        .prescriptions
                                                }{' '}
                                                receitas
                                            </span>
                                        )}
                                    </div>

                                    <div className="flex items-center justify-between pt-3 border-t border-base-300/50">
                                        <div className="flex items-center gap-2 text-xs text-base-content/60">
                                            <span>
                                                Criada em{' '}
                                                {new Date(
                                                    integration.createdAt
                                                ).toLocaleDateString('pt-BR')}
                                            </span>
                                        </div>

                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() =>
                                                    handleTest(integration.id)
                                                }
                                                className="btn btn-sm btn-ghost"
                                                title="Testar conexão"
                                                disabled={
                                                    testing === integration.id
                                                }
                                            >
                                                {testing === integration.id ? (
                                                    <span className="loading loading-spinner loading-sm"></span>
                                                ) : (
                                                    <MdRefresh className="text-lg" />
                                                )}
                                            </button>
                                            {canUpdate && (
                                                <button
                                                    onClick={() =>
                                                        navigate(
                                                            `/health-integrations/pharmacy/edit/${integration.id}`
                                                        )
                                                    }
                                                    className="btn btn-sm btn-ghost"
                                                    title="Editar"
                                                >
                                                    <MdEdit className="text-lg" />
                                                </button>
                                            )}
                                            {canDelete && (
                                                <button
                                                    onClick={() =>
                                                        handleDelete(
                                                            integration.id
                                                        )
                                                    }
                                                    className="btn btn-sm btn-ghost text-error hover:bg-error/10"
                                                    title="Excluir"
                                                >
                                                    <MdDelete className="text-lg" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <ImportDataModal
                isOpen={showImportModal}
                onClose={() => setShowImportModal(false)}
                module={ShareableModule.PHARMACY_INTEGRATION}
                moduleLabel={
                    MODULE_LABELS[ShareableModule.PHARMACY_INTEGRATION]
                }
                hideFileImport
                fetchItems={async (companyId: number) => {
                    const response =
                        await getPharmacyIntegrationsByCompany(companyId)
                    return response.success ? response.result : []
                }}
                renderItem={(item: PharmacyIntegration) => (
                    <div>
                        <div className="font-medium">{item.name}</div>
                        <div className="text-sm text-base-content/60">
                            Tipo: {PHARMACY_TYPE_LABELS[item.type]}
                            {item.active && (
                                <span className="badge badge-success badge-xs ml-2">
                                    Ativa
                                </span>
                            )}
                        </div>
                    </div>
                )}
                getItemId={(item: PharmacyIntegration) => item.id}
                getItemName={(item: PharmacyIntegration) => item.name}
                onImportComplete={() => loadIntegrations()}
            />

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false)
                    setIntegrationToDelete(null)
                }}
                onConfirm={confirmDelete}
                title="Excluir integração"
                message="Tem certeza de que deseja excluir esta integração?"
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
                loading={deleting}
            />
        </div>
    )
}
